import { GesCloudApi } from "@ext/enterprise-cloud/GesCloudApi";
import { GesCloudAnalytics } from "@ext/enterprise-cloud/ui-logic/analytics/GesCloudAnalytics";
import { createContext, type ReactNode, useContext, useEffect, useMemo } from "react";

const GesCloudAnalyticsContext = createContext<GesCloudAnalytics | null>(null);

export interface GesCloudAnalyticsProviderProps {
	children: ReactNode;
	gesCloudUrl?: string;
}

export const GesCloudAnalyticsProvider = ({ children, gesCloudUrl }: GesCloudAnalyticsProviderProps) => {
	const analytics = useMemo(() => {
		if (!gesCloudUrl) return null;
		const api = new GesCloudApi(gesCloudUrl);
		return new GesCloudAnalytics((events) => api.sendAnalyticsEvents(events));
	}, [gesCloudUrl]);

	useEffect(() => {
		if (!analytics) return;

		const flush = () => analytics.flush();
		const flushWhenHidden = () => {
			if (document.visibilityState === "hidden") analytics.flush();
		};

		document.addEventListener("visibilitychange", flushWhenHidden);
		window.addEventListener("pagehide", flush);
		return () => {
			document.removeEventListener("visibilitychange", flushWhenHidden);
			window.removeEventListener("pagehide", flush);
			analytics.flush();
		};
	}, [analytics]);

	return <GesCloudAnalyticsContext.Provider value={analytics}>{children}</GesCloudAnalyticsContext.Provider>;
};

/** `null` outside GES Cloud: there is nowhere to send events to. */
export const useGesCloudAnalytics = () => useContext(GesCloudAnalyticsContext);
